import { observer } from 'mobx-react-lite'
import { PageLayout } from '@/features/jobs/details/components/PageLayout'
import { ProInfoSectionContainer } from '@/features/jobs/details/components/ProInfoSection'
import { ModalResponsesSectionContainer } from '@/features/jobs/details/components/ModalResponsesSection'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { AppointmentSectionContainer } from '@/features/jobs/details/consultation/components/AppointmentSection'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { PersonalInfoSectionContainer } from '@/features/jobs/details/tax-type/info-gathering/components/ChecklistItemsSections/PersonalInfoSectionContainer'
import { BusinessInfoSectionContainer } from '@/features/jobs/details/tax-type/info-gathering/components/ChecklistItemsSections/BusinessInfoSectionContainer'
import { NotesSectionContainer } from '@/features/jobs/details/tax-type/info-gathering/components/ChecklistItemsSections/NotesSectionContainer'
import { SupportSectionContainer } from '@/features/jobs/details/components/SupportSection/SupportSectionContainer'
import { TxfJobSectionContainer } from '@/features/jobs/details/tax-type/txf/components/TxfSectionContainer'
import { RelationManagerSection } from '@/features/relation-manager/components/RelationManagerJobDetailsSection'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { Icon } from '@/components/Icon'
import { useExpectationsDialog } from '@/features/jobs/details/components/ExpectationsDialog'
import {
  ProgressionBar,
  ProgressionBarIndicator,
  ProgressionBarLink,
  ProgressionBarNextStep,
  ProgressionBarStep,
} from '@/features/jobs/details/components/ProgressionBar'
import {
  computeProgressionFlow,
  isExtensionFlow,
} from '@/features/jobs/taxes/progression/progression-content'
import { TaxReturnAmountsSectionContainer } from '@/features/jobs/details/tax-type/components/TaxReturnAmounts/TaxReturnAmountsContainers'

interface TaxJobOverviewTabProps {
  vm: JobDetailsViewModel
  taxVm: TaxJobDetailsViewModel
  displayContactSupport: boolean
}

/**
 * Tax job overview tab.
 */
export const TaxJobOverviewTab = observer(function TaxJobOverviewTab({
  vm,
  taxVm,
  displayContactSupport,
}: TaxJobOverviewTabProps) {
  return (
    <PageLayout
      top={<ProgressionBarSection taxVm={taxVm} />}
      asideTop={
        <>
          <ProInfoSectionContainer vm={vm} />

          {vm.relationManager && (
            <RelationManagerSection
              fullName={vm.relationManager.fullName}
              avatarUrl={vm.relationManager.avatarUrl}
              callLink={vm.relationManager.meetingLink}
            />
          )}
        </>
      }
      asideBottom={displayContactSupport && <SupportSectionContainer />}
    >
      <TaxReturnAmountsSectionContainer vm={taxVm} />

      <TxfJobSectionContainer
        vm={taxVm.txfJobVm}
        sectionHeadline="Tax information"
      />

      {taxVm.consultationManagerVm.consultation && (
        <AppointmentSectionContainer vm={taxVm.consultationManagerVm} />
      )}
      <PersonalInfoSectionContainer vm={taxVm.infoGatheringVm} />
      <BusinessInfoSectionContainer vm={taxVm.infoGatheringVm} />
      <NotesSectionContainer vm={taxVm.infoGatheringVm.pageVm} />
      <ModalResponsesSectionContainer
        vm={vm.questionsDisplayVm}
        sectionHeadline={
          taxVm.hasTxfIntegration ? 'Your responses' : 'Tax information summary'
        }
        dialogHeadline="View your tax information summary"
      />
    </PageLayout>
  )
})

interface ProgressionBarSectionProps {
  taxVm: TaxJobDetailsViewModel
}

/**
 * Progression Bar Section
 */
const ProgressionBarSection = observer(function ProgressionBarSection({
  taxVm,
}: ProgressionBarSectionProps) {
  const isSpacious = useIsSpacious()
  const { open: openExpectationsDialog } = useExpectationsDialog()
  const progression = taxVm.progressionVm.progression

  if (!progression) {
    return null
  }

  const progressionFlow = computeProgressionFlow({
    flow: progression.flow,
    steps: progression.recordedSteps.items.map((s) => s.step),
    isExtensionOnlyJob: isExtensionFlow(progression.flow),
    ignoreVisibility: false,
  })

  const currentStepIndex = progressionFlow.findIndex(
    (s) => s.step === progression.current,
  )
  const nextStep = progressionFlow[currentStepIndex + 1]

  return (
    <ProgressionBar>
      <ProgressionBarStep>
        {`Step ${currentStepIndex + 1} of ${progressionFlow.length}`}
      </ProgressionBarStep>
      {isSpacious && (
        <ProgressionBarIndicator
          currentStep={currentStepIndex + 1}
          totalSteps={progressionFlow.length}
        />
      )}
      <ProgressionBarNextStep
        title={isSpacious ? nextStep?.content.title : undefined}
      >
        <ProgressionBarLink
          onPress={openExpectationsDialog}
          testId="tax-job-details-header-view-expectations-dialog-button"
        >
          See all steps <Icon mini name="chevron" />
        </ProgressionBarLink>
      </ProgressionBarNextStep>
    </ProgressionBar>
  )
})
